import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconButton, Chip } from '@mui/material';
import { MoreVert, Tune, Description } from '@mui/icons-material';
import Menus from '../Dialog/Menus';
import { EVENT_STATUS, EVENT_TYPES } from '../../app/store/constant';
import { formatEventDate } from '../../utils/dateFormatter';
import { H4, Body2, Body3 } from '../../themes/typography';

const eventImg =
    'https://images.unsplash.com/photo-1719937051176-9b98352a6cf4?w=600&amp;auto=format&amp;fit=crop&amp;q=60&amp;ixlib=rb-4.0.3&amp;ixid=M3wxMjA3fDF8MHxmZWF0dXJlZC1waG90b3MtZmVlZHwxfHx8ZW58MHx8fHx8';

export default function EventListCard({
    name = 'AI Innovations and Future',
    venueName,
    latitude = 21.1754615,
    longitude = 72.7830602,
    totalTickets = 500,
    soldTickets = 0,
    revenue = 'Pending',
    eventThumbnail = eventImg,
    isPublicEvent,
    // eventId = "12345",
    status,
    startDate = '2025-09-12',
    startTime = '10:00 AM',
    endDate = 'Mon Sep 25, 2023',
    endTime = '5:00 PM',
    eventType = 'single_day',
    schedule = [],
    slug,
    isPublishable = {},
    createdAt,
    updatedAt
}) {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const menusList = [
        {
            id: 'manageEvent',
            icon: <Tune style={{ color: 'var(--tertiary-color)', fontSize: '1.5rem' }} />,
            menuName: 'Manage Event',
            onClick: () => navigate(`/event/${slug}/event-details`)
        },
        {
            id: 'eventReport',
            icon: <Description style={{ color: 'var(--tertiary-color)', fontSize: '1.5rem' }} />,
            menuName: 'Event Report',
            onClick: () => navigate(`/event/${slug}/reports`)
        }
    ];

    function handleClose() {
        setAnchorEl(null);
    }

    function handleClick(event) {
        setAnchorEl(event.currentTarget);
    }

    function getEventStatusVariant(status) {
        switch (status) {
            case EVENT_STATUS.live:
                return 'live';
            case EVENT_STATUS.cancelled:
            case EVENT_STATUS.ended:
                return 'soldOut';
            default:
                return 'scheduled';
        }
    }

    function getEventStatusText(status) {
        switch (status) {
            case EVENT_STATUS.live:
                return 'Live';
            case EVENT_STATUS.drafted:
                return 'Drafted';
            case EVENT_STATUS.cancelled:
                return 'Cancelled';
            case EVENT_STATUS.ended:
                return 'Event Ended';
            default:
                return 'Scheduled';
        }
    }

    function getEventTypeText(eventType) {
        switch (eventType) {
            case 'multi_day':
                return 'Multi Date';
            case 'continuous':
                return 'Continuous';
            default:
                return 'Single Date';
        }
    }

    function changeStartingOnText(status) {
        if (status === EVENT_STATUS.live) {
            return 'Ending on :';
        } else if (status === EVENT_STATUS.ended) {
            return 'Ended on :';
        } else if (status === EVENT_STATUS.cancelled) {
            return 'Cancelled on :';
        } else if (status === EVENT_STATUS.drafted && !isPublishable?.schedule) {
            return 'Created on :';
        } else if (status === EVENT_STATUS.drafted && isPublishable?.schedule) {
            return 'Drafted on :';
        } else {
            return 'Starting on :';
        }
    }

    function AddEventScheduleChip() {
        // Check if it's a single day event
        if (eventType === EVENT_TYPES.singleDay) {
            // Check if schedule exists and has at least one entry with slots
            if (
                schedule &&
                schedule.length > 0 &&
                schedule[0]?.slots &&
                schedule[0]?.slots.length > 0
            ) {
                const slotsCount = schedule[0]?.slots?.length;
                return `${slotsCount} slots`;
            }
        }
        // Check if it's a multi-day event
        else if (eventType === 'multi_day') {
            // Check if schedule exists
            if (schedule && schedule.length > 0) {
                // Count total slots across all schedule dates
                let totalSlots = 0;

                // Loop through each schedule day and count slots
                schedule.forEach((day) => {
                    if (day.slots && day.slots.length > 0) {
                        totalSlots += day.slots.length;
                    }
                });

                // Only return if there are slots
                if (totalSlots > 0) {
                    return `${totalSlots} slots`;
                }
            }
        }
        return null;
    }

    const addEventScheduleChip = AddEventScheduleChip();

    function getEventDate() {
        if (EVENT_STATUS.live === status) {
            return `${formatEventDate(startDate)} at ${startTime}`;
        } else if (EVENT_STATUS.ended === status) {
            return `${formatEventDate(endDate)} at ${endTime}`;
        } else if (EVENT_STATUS.cancelled === status) {
            return `${formatEventDate(endDate)} at ${endTime}`;
        } else if (status === EVENT_STATUS.drafted && !isPublishable?.schedule) {
            const createdDate = new Date(createdAt);
            const formattedDate = createdDate.toLocaleDateString('en-US', {
                weekday: 'short',
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
            const formattedTime = createdDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            return `${formattedDate} at ${formattedTime}`;
        } else if (status === EVENT_STATUS.drafted && isPublishable?.schedule) {
            const updatedDate = new Date(updatedAt);
            const formattedDate = updatedDate.toLocaleDateString('en-US', {
                weekday: 'short',
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
            const formattedTime = updatedDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            return `${formattedDate} at ${formattedTime}`;
        } else {
            return `${formatEventDate(startDate)} at ${startTime}`;
        }
    }
    const eventDate = getEventDate();

    return (
        <div id="my-event-list-card-container">
            <div className="event-list-card-main">
                <div className="event-list-card-main-wrapper">
                    <div className="event-list-card-image">
                        <img src={eventThumbnail} alt="Event" />
                    </div>

                    <div className="main-event-list-card-details">
                        <div className="event-list-card-name-and-status">
                            <Link
                                to={`/event/${slug}/dashboard`}
                                className="event-list-card-name-link"
                            >
                                <H4 fontWeight={600} color="var(--secondary-color)">
                                    {name}
                                </H4>
                            </Link>
                            <div>
                                <Chip
                                    label={getEventStatusText(status)}
                                    variant={getEventStatusVariant(status)}
                                />
                            </div>
                        </div>

                        <div className="event-list-card-main-detail-container">
                            <div className="event-list-card-details-container">
                                <div className="event-list-card-location-detail">
                                    <div className="event-list-card-location-label">
                                        <Body2>Venue :</Body2>
                                    </div>
                                    <div className="event-list-card-location-value">
                                        {venueName ? (
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
                                                className="event-list-card-location-link"
                                                target="_blank"
                                            >
                                                {venueName}
                                            </a>
                                        ) : (
                                            <Body2 fontWeight={400}>Venue not added</Body2>
                                        )}
                                    </div>
                                </div>

                                <div className="event-list-card-location-detail">
                                    <div className="event-list-card-location-label">
                                        <Body2>Ticketing :</Body2>
                                    </div>
                                    <div className="event-list-card-location-value">
                                        <Body2 fontWeight={400}>
                                            {soldTickets === 0 && totalTickets === 0
                                                ? 'No Tickets Added'
                                                : `${soldTickets} / ${totalTickets}`}
                                        </Body2>
                                    </div>
                                </div>
                            </div>

                            <div className="event-list-card-details-container">
                                <div className="event-list-card-location-detail">
                                    <div className="event-list-card-location-label">
                                        <Body2>{changeStartingOnText(status)}</Body2>
                                    </div>
                                    <div className="event-list-card-location-value">
                                        <Body2 fontWeight={400}>{eventDate}</Body2>
                                    </div>
                                </div>

                                <div className="event-list-card-location-detail">
                                    <div className="event-list-card-location-label">
                                        <Body2>Revenue :</Body2>
                                    </div>
                                    <div className="event-list-card-location-value">
                                        <Body2 fontWeight={400}>{revenue}</Body2>
                                    </div>
                                </div>
                            </div>

                            <div className="event-list-card-details-container">
                                <div className="event-list-card-location-detail">
                                    <div className="event-list-card-location-label">
                                        <Body2>Event Visibility :</Body2>
                                    </div>
                                    <div className="event-list-card-location-value">
                                        <Body2 fontWeight={400}>
                                            {!isPublicEvent ? 'Public' : 'Private'}
                                        </Body2>
                                    </div>
                                </div>
                            </div>

                            <div className="event-list-card-details-container">
                                <div className="event-list-card-location-detail">
                                    <Chip
                                        color="normal"
                                        label={
                                            <Body3 color="var(--tertiary-color)">
                                                {getEventTypeText(eventType)}
                                            </Body3>
                                        }
                                    />
                                    {addEventScheduleChip && (
                                        <Chip
                                            color="normal"
                                            label={
                                                <Body3 color="var(--tertiary-color)">
                                                    {addEventScheduleChip}
                                                </Body3>
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="event-list-card-elipsis">
                            <IconButton
                                onClick={handleClick}
                                aria-controls={open ? 'event-manage' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                            >
                                <MoreVert sx={{ fontSize: '1.75rem' }} />
                            </IconButton>
                            <Menus
                                id="ticket-menus"
                                open={open}
                                anchorEl={anchorEl}
                                onClick={handleClose}
                                onClose={handleClose}
                                menus={menusList}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* <div className="event-list-card-quick-action"></div> */}
        </div>
    );
}
